// eslint-disable-next-line no-restricted-imports
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import clsx from "clsx";
import { partition } from "lodash-es";
import { FC, useEffect, useId, useMemo } from "react";

import { PlannerInsert } from "../section/planner/PlannerInsert";

import { SessionInProgressButton } from "./SessionInProgressButton";
import { isParticipant } from "./roles";

import { serverGetHomePlannerPages, serverGetPageAssessmentStatuses } from "api/api-server-data";
import type { CoreUserRoleType, SectionType, TermType } from "components/server-types";
import { t } from "i18n/i18n";
import { EmptyCard } from "mds/components/EmptyCard";
import { IconText } from "mds/components/IconText";
import { ListItem } from "mds/components/ListItem";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { BookSectionIcon, ChevronDownIcon, ChevronUpIcon, EyeNotVisibleIcon } from "mds/icons";
import { slices, storeApi, useAppDispatch, useAppSelector } from "store/index";
import { courseSelectors, selectSectionsForCurrentUser, termSelectors } from "store/selectors";
import { sortByTermYearRegex } from "store/selectors/sorting";
import { computedStateActions } from "store/slices/computed";
import { getCourseAndSectionDisplayTitle } from "utils/course";
import { useIsArchived } from "utils/hooks/use-is-archived";
import { HomeRoleType, sectionHomeUrl } from "utils/urls";

type TermWithSections = { term: TermType; sections: SectionType[] };

const TermsWithSectionsDisclosure: FC<{
  id: string;
  termsWithSections: TermWithSections[];
  archived?: boolean;
}> = ({ id, termsWithSections, archived = false }) => {
  const { isArchived } = useIsArchived();
  const coursesById = useAppSelector(courseSelectors.selectEntities);

  return (
    <>
      {termsWithSections.map((termWithSections) => (
        <Disclosure key={termWithSections.term.id} defaultOpen>
          {({ open }) => {
            const titleId = `${id}-${termWithSections.term.id}`;
            return (
              <div className={clsx(archived && "ml-6")}>
                <DisclosureButton
                  className={clsx(
                    "flex w-full items-center justify-between border-b border-black-tint-90 py-2 text-base font-medium",
                    archived ? "text-black-tint-55" : "text-black-tint-10",
                  )}
                  title={open ? t("tooltip.collapse") : t("tooltip.expand")}
                >
                  <span id={titleId}>{termWithSections.term.title}</span>
                  {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </DisclosureButton>

                <DisclosurePanel className="flex flex-col gap-2">
                  <ol aria-labelledby={titleId} className="flex flex-col">
                    {termWithSections.sections
                      .map((section) => {
                        const course = coursesById[section.course_id];
                        // It's possible during the initial rendering of a page that we haven't loaded
                        // the Courses yet, so bail out if we can't find one. The selector will refresh
                        // when the Courses are eventually loaded.
                        if (!course) return null;
                        const iconText = getCourseAndSectionDisplayTitle(course, section);

                        return (
                          <ListItem
                            className="w-full justify-between first:border-t-0"
                            key={section.id}
                            kind="primary"
                            to={sectionHomeUrl(section.id)}
                          >
                            <div className="flex min-w-0 flex-auto">
                              <IconText
                                className={clsx("truncate", {
                                  "text-black-tint-55": isArchived(section),
                                })}
                                iconStart={
                                  isArchived(section) ? <EyeNotVisibleIcon /> : <BookSectionIcon />
                                }
                                text={<span className="min-w-12 truncate">{iconText}</span>}
                              />
                            </div>
                            <SessionInProgressButton sectionId={section.id} />
                          </ListItem>
                        );
                      })
                      .filter(Boolean)}
                  </ol>
                </DisclosurePanel>
              </div>
            );
          }}
        </Disclosure>
      ))}
    </>
  );
};

export const HomeSectionList: FC<{ role: HomeRoleType }> = ({ role }) => {
  const id = useId();
  const dispatch = useAppDispatch();
  const terms = useAppSelector(termSelectors.selectAll);
  const sectionsForSelectedRole = useAppSelector((s) => selectSectionsForCurrentUser(s, role));
  const { isActive, sortByActiveTitles } = useIsArchived();
  const isSmallScreen = !useIsMdOrLarger();
  const canSeePlanner = isParticipant(role);

  useEffect(() => {
    const loadPlannerData = async () => {
      const pages = await serverGetHomePlannerPages(role as CoreUserRoleType);
      if (pages?.length) {
        dispatch(slices.pages.actions.updateMany(pages));

        const pageIds = pages.map((p) => p.id);

        storeApi.page_access_data.list({ page_id__in: pageIds });
        const statuses = await serverGetPageAssessmentStatuses({ pageIds });

        dispatch(computedStateActions.savePageAssessmentStatuses(statuses));
      }
    };

    if (canSeePlanner) {
      loadPlannerData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeePlanner, role]);

  const termsWithSections = useMemo(
    () =>
      terms.toSorted(sortByTermYearRegex).map((term) => {
        const sectionsForTerm = sectionsForSelectedRole
          .filter((section) => section.term_id === term.id)
          .toSorted(sortByActiveTitles);
        return {
          term,
          sections: sectionsForTerm,
        };
      }),
    [sectionsForSelectedRole, terms, sortByActiveTitles],
  ).filter((term) => term.sections.length > 0);

  const [activeTermsWithSections, archivedTermsWithSections] = partition(
    termsWithSections,
    (term) => {
      return !term.term.archived_at && term.sections.some((section) => isActive(section));
    },
  );

  if (sectionsForSelectedRole.length === 0) {
    return (
      <EmptyCard
        className="mb-4"
        description={t("home.no_sections_for_role_description")}
        icon={<BookSectionIcon className="text-blue" />}
        title={t("home.no_sections_for_role_title")}
      />
    );
  }

  return (
    <div className="flex h-full flex-col-reverse gap-6 overflow-y-auto md:flex-row md:items-start md:gap-10">
      <div className="flex flex-1 flex-col gap-8">
        <TermsWithSectionsDisclosure id={id} termsWithSections={activeTermsWithSections} />
        {archivedTermsWithSections.length > 0 && (
          <Disclosure key="archived-terms" defaultOpen>
            {({ open }) => {
              const titleId = `${id}-archived-terms`;
              return (
                <div>
                  <DisclosureButton
                    className="flex w-full items-center justify-between border-b border-black-tint-90 py-2 text-base font-medium text-black-tint-55"
                    title={open ? t("tooltip.collapse") : t("tooltip.expand")}
                  >
                    <span id={titleId}>{t("home.terms.archived")}</span>
                    {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  </DisclosureButton>

                  <DisclosurePanel className="flex flex-col gap-2">
                    <TermsWithSectionsDisclosure
                      id={id}
                      termsWithSections={archivedTermsWithSections}
                      archived
                    />
                  </DisclosurePanel>
                </div>
              );
            }}
          </Disclosure>
        )}
      </div>

      {canSeePlanner && (
        <div className={clsx("flex-shrink-0", !isSmallScreen && "mb-6")}>
          <PlannerInsert role={role as CoreUserRoleType} startOpen={!isSmallScreen} />
        </div>
      )}
    </div>
  );
};
